/**
 * Builds a normalized URL by joining a base URL with a path or querystring.
 *
 * This function:
 * - Removes trailing slashes from the base
 * - Removes leading slashes from the path/query
 * - Ensures the result has a single slash between them
 *
 * @param base - The base URL (e.g., 'http://example.com/')
 * @param pathAndQuerystring - The path or query string (e.g., '/api?id=1')
 * @returns A fully composed URL with proper slash normalization.
 *
 * @example
 * buildUrl('http://localhost/', '/path');  // 'http://localhost/path'
 * buildUrl('http://test.com', '?value=1'); // 'http://test.com/?value=1'
 */
export function buildUrl(base: string, pathAndQuerystring: string): string {
  const trailingSlashRegex = /\/+$/;
  const leadingSlashRegex = /^\/+/;

  const cleanBase = base.replace(trailingSlashRegex, '');
  const cleanPath = pathAndQuerystring.replace(leadingSlashRegex, '');

  return `${cleanBase}/${cleanPath}`;
}

/**
 * Determines whether the given hostname represents a localhost environment.
 *
 * By default, it checks the browser's `location.hostname`, but you can
 * supply a value for testing or server-side rendering environments.
 *
 * Localhost matches:
 * - 'localhost'
 * - '127.0.0.1'
 * - '[::1]'
 * - '' (e.g. file:// protocol or missing host)
 *
 * @param hostname - The hostname to check. Defaults to `location.hostname`.
 * @returns `true` if the hostname is a localhost address.
 *
 * @example
 * isLocalhost(); // true if run on localhost
 * isLocalhost('127.0.0.1'); // true
 * isLocalhost('example.com'); // false
 */
export function isLocalhost(hostname: string = location.hostname): boolean {
  return ['localhost', '127.0.0.1', '[::1]', ''].includes(hostname);
}
